import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  palette,
  spacing,
  radius,
  shadow,
  typography,
  zIndex,
  layout,
} from '../../../style-utils/theme';
import { PrimaryButton, SecondaryButton } from '../onboarding.styled';
import { down } from '../../../style-utils/media-queries';
import { useDialogFocus } from '../../hooks/use-dialog-focus';
import { ModalPortal } from '../../modal/modal-portal';
import { Presence, m, scrimMotion, useOverlayMotion } from '../../../motion';

const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};
  /* Prefixed AND unprefixed, the way every other frosted surface in the app
     writes it (install-prompt, sidebar, the dashboard scrims). Safari still
     ships this behind -webkit-, and the unprefixed line alone left this one
     scrim unblurred while its siblings blurred. */
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Modal = m.div`
  width: 100%;
  max-width: 420px;
  background: ${palette.surface};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  padding: ${spacing.lg};
  text-align: center;

  ${down('sm')} {
    max-width: none;
    max-height: calc(100vh - ${spacing.sm});
    max-height: calc(100dvh - ${spacing.sm});
    overflow-y: auto;
    border-radius: ${radius.lg} ${radius.lg} 0 0;
    padding: ${spacing.md} ${spacing.sm};
  }
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};
`;

const Sub = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  line-height: ${typography.leading.normal};
`;

// Shared with the guest pass page (Plan 6, UI-CONTRACT §7).
export const Qr = styled.img`
  width: 220px;
  max-width: 100%;
  height: auto;
  aspect-ratio: 1;
  border-radius: ${radius.sm};
  border: 1px solid ${palette.line};
  padding: ${spacing.xs};
  background: ${palette.surface};
`;

// Shared with the resident pass share (Plan 6, UI-CONTRACT §4 Share).
export const LinkRow = styled.div`
  display: flex;
  gap: ${spacing.xs};
  margin: ${spacing.md} 0 0;

  ${down('sm')} {
    flex-direction: column;

    & > button {
      width: 100%;
    }

    /* In a COLUMN, the field's flex: 1 (basis 0) is its HEIGHT, and it
       collapsed the 48px field to its 18px line box — measured by the persona
       journey on an iPhone (Plan 6 J-6.1). The field keeps its own height. */
    & > input {
      flex: 0 0 auto;
    }
  }
`;

// Shared with the resident pass share (Plan 6, UI-CONTRACT §4 Share).
export const LinkField = styled.input`
  flex: 1;
  min-width: 0;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
`;

const Actions = styled.div`
  margin-top: ${spacing.md};
`;

interface Props {
  open: boolean;
  onClose: () => void;
  inviteeName?: string;
  inviteUrl: string;
  qrDataUrl: string;
  emailSent?: boolean;
}

export const InviteQrModal: React.FC<Props> = ({
  open,
  onClose,
  inviteeName,
  inviteUrl,
  qrDataUrl,
  emailSent,
}) => {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogRef = useDialogFocus(open, onClose);
  const modalMotion = useOverlayMotion('modal');

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
      setCopied(false);
    }
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Presence>
      {open ? (
        <ModalPortal key="invite-qr-modal">
          <Scrim {...scrimMotion} onClick={onClose}>
            <Modal
              {...modalMotion}
              ref={dialogRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-labelledby="invite-ready-title"
              onClick={e => e.stopPropagation()}
            >
              <Title id="invite-ready-title">Invitation ready</Title>
              <Sub>
                {emailSent
                  ? `We emailed the invite${
                      inviteeName ? ` to ${inviteeName}` : ''
                    }. They can also scan this QR to join.`
                  : `Share this QR or link${
                      inviteeName ? ` with ${inviteeName}` : ''
                    } — they scan or open it to set a password and join.`}
              </Sub>
              <Qr src={qrDataUrl} alt="Invite QR code" />
              <LinkRow>
                <LinkField
                  readOnly
                  value={inviteUrl}
                  onFocus={e => e.target.select()}
                />
                <SecondaryButton type="button" onClick={copy}>
                  {copied ? 'Copied' : 'Copy'}
                </SecondaryButton>
              </LinkRow>
              <Actions>
                <PrimaryButton
                  type="button"
                  onClick={onClose}
                  style={{ width: '100%' }}
                >
                  Done
                </PrimaryButton>
              </Actions>
            </Modal>
          </Scrim>
        </ModalPortal>
      ) : null}
    </Presence>
  );
};
